import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/router";
import { observer } from "mobx-react-lite";
import { Modal } from "antd";
import cx from "classnames";
import store from "store";
import Storage from "utils/storage";
import XtLoading from "@/components/Xt/Loading";
import AppModalTransfer from "@/components/App/modal/transfer";
import useUserLogin from "@/components/Pages/Trade/_hook/useUserLogin";
import styles from "./index.module.scss";
import { clampGuideHighlightTopPx } from "./_cmpt/newbieGuide/cfdGuideViewport";

// 导入差价合约组件
import Header from "./_cmpt/header";
import Kline from "./_cmpt/kline";
import TradeForm from "./_cmpt/tradeForm";
import TradeFormPro from "./_cmpt/tradeFormPro";
import OrderBook from "./_cmpt/orderBook";
import PositionList from "./_cmpt/positionList";
import StandardGuide from "./_cmpt/newbieGuide/StandardGuide";
import StandardGuideStep2 from "./_cmpt/newbieGuide/StandardGuideStep2";
import StandardGuideStep3 from "./_cmpt/newbieGuide/StandardGuideStep3";
import StandardGuideStep4 from "./_cmpt/newbieGuide/StandardGuideStep4";
import StandardGuideStep5 from "./_cmpt/newbieGuide/StandardGuideStep5";
import StandardGuideStep6 from "./_cmpt/newbieGuide/StandardGuideStep6";
import StandardGuideStep7 from "./_cmpt/newbieGuide/StandardGuideStep7";
import ProGuide from "./_cmpt/newbieGuide/ProGuide";
import ProGuideStep2 from "./_cmpt/newbieGuide/ProGuideStep2";
import ProGuideStep3 from "./_cmpt/newbieGuide/ProGuideStep3";
import ProGuideStep4 from "./_cmpt/newbieGuide/ProGuideStep4";
import ProGuideStep5 from "./_cmpt/newbieGuide/ProGuideStep5";
import ProGuideStep6 from "./_cmpt/newbieGuide/ProGuideStep6";
import ProGuideStep7 from "./_cmpt/newbieGuide/ProGuideStep7";
import ProGuideStep8 from "./_cmpt/newbieGuide/ProGuideStep8";
import ProGuideStep9 from "./_cmpt/newbieGuide/ProGuideStep9";
import { TradeFooter } from "@/components/Pages/Trade/_cmpt/tradeFooter";
import CPMT_suspended from "@/components/Pages/Trade/_cmpt/suspended";

export type CfdTradeDefaultViewMode = "standard" | "pro";

export interface CfdTradeProps {
  /** 仅影响首次进入时的布局；不传则与历史行为一致为 standard（如 /cfd 合约页） */
  defaultViewMode?: CfdTradeDefaultViewMode;
  /** 仅 Prop 交易页：下单成功后刷新 GET /prop/trading/context（头部场次/倒计时等；余额以 balances 接口为准） */
  refreshPropTradingContext?: () => void | Promise<void>;
  /** 仅展示用：覆盖「可用余额」行币种文案（如 Prop 页 Avbl 后显示 USDX）；不传则沿用交易对计价币种 */
  availableBalanceDisplayUnit?: string;
  /** 隐藏交易对切换按钮（如 Prop Trading 交易页不允许切换交易对） */
  hideMarketSwitch?: boolean;
}

function getInitialCfdTradeViewMode(defaultViewMode?: CfdTradeDefaultViewMode): "standard" | "pro" {
  if (typeof window === "undefined") {
    return defaultViewMode === "pro" ? "pro" : "standard";
  }
  if (defaultViewMode === "pro") return "pro";
  const v = Storage.get("cfdTradeViewMode");
  return v === "pro" ? "pro" : "standard";
}

const CfdTrade: React.FC<CfdTradeProps> = ({
  defaultViewMode,
  refreshPropTradingContext,
  availableBalanceDisplayUnit,
  hideMarketSwitch,
}) => {
  const router = useRouter();
  const usePropOpenApi = Boolean((router.pathname || "").includes("/proptrading/trade"));
  const [viewMode, setViewMode] = useState<"standard" | "pro">(() =>
    getInitialCfdTradeViewMode(defaultViewMode)
  );
  const handleViewModeChange = useCallback((mode: "standard" | "pro") => {
    setViewMode(mode);
    Storage.set("cfdTradeViewMode", mode);
  }, []);
  const [isKlineExpanded, setIsKlineExpanded] = useState(false); // K线图展开状态
  const positionListRef = useRef<{ refresh: (showLoading?: boolean) => void }>(null);
  const { isH5 } = store.app;

  // 标准版新手引导（第 1 页、第 2 页、第 3 页、第 4 页、第 5 页和第 6 页）
  const [standardGuideStep1Open, setStandardGuideStep1Open] = useState(false);
  const [standardGuideStep2Open, setStandardGuideStep2Open] = useState(false);
  const [standardGuideStep3Open, setStandardGuideStep3Open] = useState(false);
  const [standardGuideStep4Open, setStandardGuideStep4Open] = useState(false);
  const [standardGuideStep5Open, setStandardGuideStep5Open] = useState(false);
  const [standardGuideStep6Open, setStandardGuideStep6Open] = useState(false);
  const [standardGuideStep7Open, setStandardGuideStep7Open] = useState(false);

  // 专业版新手引导（第 1 页、第 2 页、第 3 页、第 4 页、第 5 页、第 6 页、第 7 页、第 8 页、第 9 页）
  const [proGuideStep1Open, setProGuideStep1Open] = useState(false);
  const [proGuideStep2Open, setProGuideStep2Open] = useState(false);
  const [proGuideStep3Open, setProGuideStep3Open] = useState(false);
  const [proGuideStep4Open, setProGuideStep4Open] = useState(false);
  const [proGuideStep5Open, setProGuideStep5Open] = useState(false);
  const [proGuideStep6Open, setProGuideStep6Open] = useState(false);
  const [proGuideStep7Open, setProGuideStep7Open] = useState(false);
  const [proGuideStep8Open, setProGuideStep8Open] = useState(false);
  const [proGuideStep9Open, setProGuideStep9Open] = useState(false);

  // 判断是否有任何步骤打开（标准版或专业版）
  const isAnyGuideOpen = standardGuideStep1Open || standardGuideStep2Open || standardGuideStep3Open || standardGuideStep4Open || standardGuideStep5Open || standardGuideStep6Open || standardGuideStep7Open || proGuideStep1Open || proGuideStep2Open || proGuideStep3Open || proGuideStep4Open || proGuideStep5Open || proGuideStep6Open || proGuideStep7Open || proGuideStep8Open || proGuideStep9Open;

  /** 交易主区域 ref：引导层挂到 body 后，用其 getBoundingClientRect 对齐蒙层坐标系与页面内容 */
  const cfdMainRef = useRef<HTMLDivElement>(null);
  const [guideStageRect, setGuideStageRect] = useState({ top: 0, left: 0, width: 375, height: 800 });

  useLayoutEffect(() => {
    if (!isAnyGuideOpen) return;
    const el = cfdMainRef.current;
    if (!el) return;
    let rafId = 0;
    const update = () => {
      const r = el.getBoundingClientRect();
      setGuideStageRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    const scheduleUpdate = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, true);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate, true);
    };
  }, [isAnyGuideOpen]);

  // 使用 useUserLogin hook 来获取和更新余额（含 refreshBalance 供下单后/定时刷新）
  const { refreshBalance } = useUserLogin();

  // 当模式切换时，重置 K 线图展开状态
  useEffect(() => {
    if (viewMode === "standard") {
      setIsKlineExpanded(true); // 标准模式始终展开
    } else {
      setIsKlineExpanded(false); // 专业模式默认收起
    }
  }, [viewMode]);

  //store 初始化
  useEffect(() => {
    store.app.initLayout();
    store.app.getServerTime();
    store.market.getCfdMarketConfig(); // 获取CFD市场配置
  }, []);

  // 客户端：用 URL 的 symbol 更新 store，获取不到配置也不默认跳转
  useEffect(() => {
    const { symbol } = router.query;
    // 非 /cfd 路由复用时（如 proptrading 交易页），router.query.symbol 可能为空：
    // 优先沿用 store.market.name，其次默认 U 本位 btc_usdt_cfd
    const name = symbol && typeof symbol === "string" ? symbol : store.market.name || "btc_usdt_cfd";
    store.market.updateState({ name });
  }, [router.query.symbol, router, store.market.name]);

  //客户端是否初始化完毕
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!store.app.clientSideReady || !store.market.config) return;
    setLoading(false);
    // 引导进行中保持 body 滚动锁定，避免与步骤内 scrollTo 互相打架
    if (!isAnyGuideOpen) {
      document.body.style.removeProperty("overflow");
    }
    console.log("%c【CFD Trade ready】", "color:#52c41a");
  }, [store.app.clientSideReady, store.market.config, isAnyGuideOpen]);

  // 任意引导步骤打开时锁定页面滚动；全部关闭后再恢复
  useEffect(() => {
    if (!isAnyGuideOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.removeProperty("overflow");
    };
  }, [isAnyGuideOpen]);

  // 首次进入标准版（H5）弹出新手引导第 1 页，仅自动打开一次（记录 storage）
  useEffect(() => {
    if (loading) return;
    if (!isH5) return;
    if (viewMode !== "standard") return;
    if (typeof window === "undefined") return;
    const known = Storage.get("cfdStandardGuideKnown");
    if (known) return;
    setStandardGuideStep1Open(true);
  }, [loading, isH5, viewMode]);

  // 首次进入专业版（H5）弹出新手引导第 1 页，仅自动打开一次（记录 storage）
  useEffect(() => {
    if (loading) return;
    if (!isH5) return;
    if (viewMode !== "pro") return;
    if (typeof window === "undefined") return;
    const known = Storage.get("cfdProGuideKnown");
    if (known) return;
    setProGuideStep1Open(true);
  }, [loading, isH5, viewMode]);

  // 标准版 / 专业版第一步打开时强制回顶，避免交易对等顶部信息被滚出视口（占位条、恢复滚动位等）
  useLayoutEffect(() => {
    if (!standardGuideStep1Open && !proGuideStep1Open) return;
    if (typeof window === "undefined") return;
    const goTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    goTop();
  }, [standardGuideStep1Open, proGuideStep1Open]);

  // 点击「交易教程」打开当前模式的新手引导第一步
  const handleOpenTutorial = useCallback(() => {
    window.scrollTo(0, 0);
    if (viewMode === "standard") {
      setStandardGuideStep1Open(true);
    } else {
      setProGuideStep1Open(true);
    }
  }, [viewMode]);

  // 关闭所有引导（跳过或完成），并写入 storage 标记已看过，下次不再自动弹出
  const closeAllGuides = useCallback(() => {
    // 判断当前是标准版还是专业版引导，分别存储标记
    const isStandardGuideActive = standardGuideStep1Open || standardGuideStep2Open || standardGuideStep3Open || standardGuideStep4Open || standardGuideStep5Open || standardGuideStep6Open || standardGuideStep7Open;
    const isProGuideActive = proGuideStep1Open || proGuideStep2Open || proGuideStep3Open || proGuideStep4Open || proGuideStep5Open || proGuideStep6Open || proGuideStep7Open || proGuideStep8Open || proGuideStep9Open;

    if (isStandardGuideActive) {
      Storage.set("cfdStandardGuideKnown", true);
    }
    if (isProGuideActive) {
      Storage.set("cfdProGuideKnown", true);
    }

    setStandardGuideStep1Open(false);
    setStandardGuideStep2Open(false);
    setStandardGuideStep3Open(false);
    setStandardGuideStep4Open(false);
    setStandardGuideStep5Open(false);
    setStandardGuideStep6Open(false);
    setStandardGuideStep7Open(false);
    // 关闭专业版引导
    setProGuideStep1Open(false);
    setProGuideStep2Open(false);
    setProGuideStep3Open(false);
    setProGuideStep4Open(false);
    setProGuideStep5Open(false);
    setProGuideStep6Open(false);
    setProGuideStep7Open(false);
    setProGuideStep8Open(false);
    setProGuideStep9Open(false);
    document.body.style.removeProperty("overflow");
    // 引导关闭后，滚动到顶部
    window.scrollTo(0, 0);
  }, [standardGuideStep1Open, standardGuideStep2Open, standardGuideStep3Open, standardGuideStep4Open, standardGuideStep5Open, standardGuideStep6Open, standardGuideStep7Open, proGuideStep1Open, proGuideStep2Open, proGuideStep3Open, proGuideStep4Open, proGuideStep5Open, proGuideStep6Open, proGuideStep7Open, proGuideStep8Open, proGuideStep9Open]);

  // 点击 Trading Tutorial 时打开当前模式的新手引导第 1 页
  const handleTutorialClick = useCallback(() => {
    window.scrollTo(0, 0);
    if (viewMode === "standard") {
      setStandardGuideStep1Open(true);
    } else {
      setProGuideStep1Open(true);
    }
  }, [viewMode]);

  // 第一步的下一步：直接切换到第二步，并滚动到目标元素
  const handleStep1Next = useCallback(() => {
    // 先打开第二步，确保蒙层不会消失
    setStandardGuideStep2Open(true);
    // 然后关闭第一步
    setStandardGuideStep1Open(false);
    // 延迟一下，确保 DOM 更新完成
    setTimeout(() => {
      // 查找目标元素
      const targetElement = document.querySelector('[data-guide-target="marginSection"]');
      if (targetElement) {
        // 获取目标元素相对于视口的位置
        const rect = targetElement.getBoundingClientRect();
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // 高亮框的 top（基于 375px 设计稿）；小屏把保证金区滚到更靠上，留出下方说明与底部按钮区
        const DESIGN_WIDTH = 375;
        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
        const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
        const highlightTop = clampGuideHighlightTopPx((isSmallScreen ? 120 : 237) * scale);
        const highlightPadding = 8 * scale; // highlightRect 的 padding
        const extraScrollUp = (isSmallScreen ? 120 : 60) * scale;

        // scrollToPosition = 当前滚动 + (目标视口 top − 期望 top − padding) + 额外上滚
        const scrollToPosition =
          currentScrollTop + (rect.top - highlightTop - highlightPadding) + extraScrollUp;

        window.scrollTo(0, Math.max(0, scrollToPosition));
      }
    }, 50);
  }, []);

  // 第二步的下一步：直接切换到第三步，并滚动到目标元素
  const handleStep2Next = useCallback(() => {
    // 先打开第三步，确保蒙层不会消失
    setStandardGuideStep3Open(true);
    // 然后关闭第二步
    setStandardGuideStep2Open(false);
    // 延迟一下，确保 DOM 更新完成
    setTimeout(() => {
      // 查找目标元素
      const targetElement = document.querySelector('[data-guide-target="leverageRow"]');
      if (targetElement) {
        // 获取目标元素相对于视口的位置
        const rect = targetElement.getBoundingClientRect();
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // 与 StandardGuideStep3 的 highlightAndTipWrapper top 一致：267/180
        const DESIGN_WIDTH = 375;
        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
        const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
        const highlightTop = clampGuideHighlightTopPx((isSmallScreen ? 180 : 267) * scale);
        // 真机留出底部空间：多往上滚动一段
        const extraScrollUp = 60 * scale;
        const scrollToPosition = currentScrollTop + (rect.top - highlightTop) + extraScrollUp;
        window.scrollTo(0, Math.max(0, scrollToPosition));
      }
    }, 50);
  }, []);

  // 第三步的下一步：切换到第四步，并将充值锚点滚到视口偏上，留出下方说明与底部按钮区
  const handleStep3Next = useCallback(() => {
    // 先打开第四步，确保蒙层不会消失
    setStandardGuideStep4Open(true);
    // 然后关闭第三步
    setStandardGuideStep3Open(false);
    // 延迟一下，确保 DOM 更新完成
    setTimeout(() => {
      const targetElement = document.querySelector('[data-guide-target="cfdStandardGuideDeposit"]');
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const DESIGN_WIDTH = 375;
        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
        const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
        const highlightTop = clampGuideHighlightTopPx((isSmallScreen ? 120 : 200) * scale);
        const extraScrollUp = (isSmallScreen ? 120 : 80) * scale;
        const scrollToPosition = currentScrollTop + (rect.top - highlightTop) + extraScrollUp;
        window.scrollTo(0, Math.max(0, scrollToPosition));
      }
    }, 50);
  }, []);

  // 第四步的下一步：切换到第五步，并滚动到目标元素
  const handleStep4Next = useCallback(() => {
    // 先打开第五步，确保蒙层不会消失
    setStandardGuideStep5Open(true);
    // 然后关闭第四步
    setStandardGuideStep4Open(false);
    // 延迟一下，确保 DOM 更新完成
    setTimeout(() => {
      // 查找目标元素
      const targetElement = document.querySelector('[data-guide-target="actionButtons"]');
      if (targetElement) {
        // 获取目标元素相对于视口的位置
        const rect = targetElement.getBoundingClientRect();
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // 高亮框的 top 是 336px（基于 375px 设计稿）
        // 高亮框是绝对定位在 guideStage 内，guideStage 是 fixed 定位，所以高亮框位置相对于视口
        // 高亮框没有 padding
        const DESIGN_WIDTH = 375;
        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
        
        // 高亮框 top 与 StandardGuideStep5 一致：276/190
        const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
        const highlightTop = clampGuideHighlightTopPx(isSmallScreen ? 190 * scale : 276 * scale);
        const extraScrollUp = 80 * scale;
        const scrollToPosition = currentScrollTop + (rect.top - highlightTop) + extraScrollUp;
        window.scrollTo(0, Math.max(0, scrollToPosition));
      }
    }, 50);
  }, []);

  // 第五步的下一步：切换到第六步，并滚动到目标元素
  const handleStep5Next = useCallback(() => {
    // 先打开第六步，确保蒙层不会消失
    setStandardGuideStep6Open(true);
    // 然后关闭第五步
    setStandardGuideStep5Open(false);
    // 延迟一下，确保 DOM 更新完成
    setTimeout(() => {
      // 查找目标元素
      const targetElement = document.querySelector('[data-guide-target="positionListTabs"]');
      if (targetElement) {
        // 获取目标元素相对于视口的位置
        const rect = targetElement.getBoundingClientRect();
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // 高亮框的 top 是 391px（基于 375px 设计稿）
        // 高亮框是绝对定位在 guideStage 内，guideStage 是 fixed 定位，所以高亮框位置相对于视口
        // 高亮框有 padding: 14px（上下），需要考虑这个偏移
        const DESIGN_WIDTH = 375;
        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
        
        // 与 StandardGuideStep6 的 highlightAndTipWrapper top 一致：231/140
        const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
        const highlightTop = clampGuideHighlightTopPx(isSmallScreen ? 140 * scale : 231 * scale);
        const highlightPadding = 14 * scale;
        const extraScrollUp = 120 * scale; /* 多往上滚动，留出底部空间适配大部分机型 */
        const scrollToPosition = currentScrollTop + (rect.top - highlightTop - highlightPadding) + extraScrollUp;
        window.scrollTo(0, Math.max(0, scrollToPosition));
      }
    }, 50);
  }, []);

  // 第六步的下一步：切换到第七步，并滚动到目标元素
  const handleStep6Next = useCallback(() => {
    // 先打开第七步，确保蒙层不会消失
    setStandardGuideStep7Open(true);
    // 然后关闭第六步
    setStandardGuideStep6Open(false);
    // 延迟一下，确保 DOM 更新完成
    setTimeout(() => {
      // 查找目标元素
      const targetElement = document.querySelector('[data-guide-target="historyButton"]');
      if (targetElement) {
        // 获取目标元素相对于视口的位置
        const rect = targetElement.getBoundingClientRect();
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // 高亮框的 top 是 397px（基于 375px 设计稿）
        // 高亮框是绝对定位在 guideStage 内，guideStage 是 fixed 定位，所以高亮框位置相对于视口
        // 高亮框没有 padding
        const DESIGN_WIDTH = 375;
        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
        
        // 与 StandardGuideStep7 的 highlightAndTipWrapper top 一致：237/150
        const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
        const highlightTop = clampGuideHighlightTopPx(isSmallScreen ? 150 * scale : 237 * scale);
        const extraScrollUp = 120 * scale; /* 多往上滚动，留出底部空间适配大部分机型 */
        const scrollToPosition = currentScrollTop + (rect.top - highlightTop) + extraScrollUp;
        window.scrollTo(0, Math.max(0, scrollToPosition));
      }
    }, 50);
  }, []);

  // 第七步的下一步：关闭所有引导
  const handleStep7Next = useCallback(() => {
    closeAllGuides();
  }, [closeAllGuides]);

  // 处理弹窗更新
  const handleModalUpdateProps = useCallback(
    (attr, props) => {
      store.trade.updateState({
        [attr]: {
          ...store.trade[attr],
          ...props,
        },
      });
    },
    []
  );

  // 路由变化，关闭对话框
  useEffect(() => {
    ["modalTransfer", "modalBorrowRepay", "modalSubscribeRedeem"].map((attr) => {
      if (store.trade[attr] && store.trade[attr].open) {
        handleModalUpdateProps(attr, { open: false });
      }
    });
    Modal.destroyAll();
  }, [router, handleModalUpdateProps]);

  // 刷新持仓、订单列表与余额（下单成功后调用，静默刷新不闪）
  const handleRefreshPositionsAndOrders = useCallback(() => {
    void (async () => {
      if (positionListRef.current) {
        positionListRef.current.refresh(false);
      }
      await refreshPropTradingContext?.();
      refreshBalance?.();
    })();
  }, [refreshBalance, refreshPropTradingContext]);

  const guidePortal =
    isAnyGuideOpen && typeof document !== "undefined"
      ? createPortal(
          <div className={styles.guideMask}>
            <div
              id="cfd-guide-stage"
              className={styles.guideStage}
              style={{
                position: "fixed",
                inset: "unset",
                top: guideStageRect.top,
                left: guideStageRect.left,
                width: guideStageRect.width,
                height: guideStageRect.height,
              }}
            >
                {/* 标准版新手引导（第 1 页） */}
                {standardGuideStep1Open && (
                  <StandardGuide onSkip={closeAllGuides} onNext={handleStep1Next} />
                )}
                
                {/* 标准版新手引导（第 2 页） */}
                {standardGuideStep2Open && (
                  <StandardGuideStep2 onSkip={closeAllGuides} onNext={handleStep2Next} />
                )}

                {/* 标准版新手引导（第 3 页） */}
                {standardGuideStep3Open && (
                  <StandardGuideStep3 onSkip={closeAllGuides} onNext={handleStep3Next} />
                )}

                {/* 标准版新手引导（第 4 页） */}
                {standardGuideStep4Open && (
                  <StandardGuideStep4 onSkip={closeAllGuides} onNext={handleStep4Next} />
                )}

                {/* 标准版新手引导（第 5 页） */}
                {standardGuideStep5Open && (
                  <StandardGuideStep5 onSkip={closeAllGuides} onNext={handleStep5Next} />
                )}

                {/* 标准版新手引导（第 6 页） */}
                {standardGuideStep6Open && (
                  <StandardGuideStep6 onSkip={closeAllGuides} onNext={handleStep6Next} />
                )}

                {/* 标准版新手引导（第 7 页） */}
                {standardGuideStep7Open && (
                  <StandardGuideStep7 onSkip={closeAllGuides} onNext={handleStep7Next} />
                )}

                {/* 专业版新手引导（第 1 页） */}
                {proGuideStep1Open && (
                  <ProGuide
                    onSkip={closeAllGuides}
                    onNext={() => {
                      setProGuideStep2Open(true);
                      setProGuideStep1Open(false);
                      setTimeout(() => {
                        const targetElement = document.querySelector('[data-guide-target="cfdStandardGuideDeposit"]');
                        if (targetElement) {
                          const rect = targetElement.getBoundingClientRect();
                          const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
                          const DESIGN_WIDTH = 375;
                          const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
                          const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
                          const highlightTop = clampGuideHighlightTopPx((isSmallScreen ? 120 : 200) * scale);
                          const extraScrollUp = (isSmallScreen ? 120 : 80) * scale;
                          const scrollToPosition = currentScrollTop + (rect.top - highlightTop) + extraScrollUp;
                          window.scrollTo(0, Math.max(0, scrollToPosition));
                        }
                      }, 50);
                    }}
                  />
                )}

                {/* 专业版新手引导（第 2 页） */}
                {proGuideStep2Open && (
                  <ProGuideStep2 onSkip={closeAllGuides} onNext={() => {
                    setProGuideStep3Open(true);
                    setProGuideStep2Open(false);
                    // 延迟一下，确保 DOM 更新完成
                    setTimeout(() => {
                      // 查找目标元素
                      const targetElement = document.querySelector('[data-guide-target="proLeverageRow"]');
                      if (targetElement) {
                        // 获取目标元素相对于视口的位置
                        const rect = targetElement.getBoundingClientRect();
                        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        
                        // 与 ProGuideStep3 的 highlightAndTipWrapper top 一致：180
                        const DESIGN_WIDTH = 375;
                        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
                        const highlightTop = clampGuideHighlightTopPx(180 * scale);
                        const extraScrollUp = 60 * scale;
                        const scrollToPosition = currentScrollTop + (rect.top - highlightTop) + extraScrollUp;
                        window.scrollTo(0, Math.max(0, scrollToPosition));
                      }
                    }, 50);
                  }} />
                )}

                {/* 专业版新手引导（第 3 页） */}
                {proGuideStep3Open && (
                  <ProGuideStep3
                    onSkip={closeAllGuides}
                    onNext={() => {
                      // 第 4 步锚点（proTradeTypeRow）紧邻杠杆行，无需再滚；避免与锚点测量补滚冲突导致蒙层闪断
                      setProGuideStep4Open(true);
                      setProGuideStep3Open(false);
                    }}
                  />
                )}

                {/* 专业版新手引导（第 4 页） */}
                {proGuideStep4Open && (
                  <ProGuideStep4 onSkip={closeAllGuides} onNext={() => {
                    setProGuideStep5Open(true);
                    setProGuideStep4Open(false);
                    // 延迟一下，确保 DOM 更新完成
                    setTimeout(() => {
                      // 查找目标元素（委托价格输入框）
                      const targetElement = document.querySelector('[data-guide-target="proPriceInput"]');
                      if (targetElement) {
                        const rect = targetElement.getBoundingClientRect();
                        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        const DESIGN_WIDTH = 375;
                        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
                        const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
                        const highlightTop = clampGuideHighlightTopPx((isSmallScreen ? 140 : 245) * scale);
                        const extraScrollUp = 80 * scale;
                        const scrollToPosition = currentScrollTop + (rect.top - highlightTop) + extraScrollUp;
                        window.scrollTo(0, Math.max(0, scrollToPosition));
                      }
                    }, 50);
                  }} />
                )}

                {/* 专业版新手引导（第 5 页） */}
                {proGuideStep5Open && (
                  <ProGuideStep5 onSkip={closeAllGuides} onNext={() => {
                    setProGuideStep6Open(true);
                    setProGuideStep5Open(false);
                    // 延迟一下，确保 DOM 更新完成
                    setTimeout(() => {
                      // 查找目标元素
                      const targetElement = document.querySelector('[data-guide-target="proQuantityRow"]');
                      if (targetElement) {
                        // 获取目标元素相对于视口的位置
                        const rect = targetElement.getBoundingClientRect();
                        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        
                        // 高亮框的 top 是 309px（基于 375px 设计稿）
                        // 高亮框是绝对定位在 guideStage 内，guideStage 是 fixed 定位，所以高亮框位置相对于视口
                        // 高亮框有 padding-top: 4px，需要考虑这个偏移
                        const DESIGN_WIDTH = 375;
                        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
                        
                        const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
                        const highlightTop = clampGuideHighlightTopPx(isSmallScreen ? 220 * scale : 309 * scale);
                        const highlightPadding = 4 * scale;
                        const extraScrollUp = 80 * scale;
                        const scrollToPosition = currentScrollTop + (rect.top - highlightTop - highlightPadding) + extraScrollUp;
                        window.scrollTo(0, Math.max(0, scrollToPosition));
                      }
                    }, 50);
                  }} />
                )}

                {/* 专业版新手引导（第 6 页） */}
                {proGuideStep6Open && (
                  <ProGuideStep6 onSkip={closeAllGuides} onNext={() => {
                    setProGuideStep7Open(true);
                    setProGuideStep6Open(false);
                    // 延迟一下，确保 DOM 更新完成
                    setTimeout(() => {
                      const targetElement = document.querySelector(
                        '#cfd-trade-form-pro [data-guide-target="proActionButtons"]'
                      );
                      if (targetElement) {
                        const rect = targetElement.getBoundingClientRect();
                        const currentScrollTop =
                          window.pageYOffset || document.documentElement.scrollTop;
                        const DESIGN_WIDTH = 375;
                        const scale =
                          typeof window !== "undefined"
                            ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH
                            : 1;
                        const isSmallScreen =
                          typeof window !== "undefined" && window.innerHeight <= 700;
                        // 较 center 更靠上，留出下方提示卡与底栏（ProGuideStep7 高亮 + 最小高度示意）
                        const highlightTop = clampGuideHighlightTopPx(
                          isSmallScreen ? 130 * scale : 210 * scale
                        );
                        const highlightPadding = 12 * scale;
                        const extraScrollUp = 120 * scale;
                        const scrollToPosition =
                          currentScrollTop +
                          (rect.top - highlightTop - highlightPadding) +
                          extraScrollUp;
                        window.scrollTo(0, Math.max(0, scrollToPosition));
                      }
                    }, 50);
                  }} />
                )}

                {/* 专业版新手引导（第 7 页） */}
                {proGuideStep7Open && (
                  <ProGuideStep7 onSkip={closeAllGuides} onNext={() => {
                    setProGuideStep8Open(true);
                    setProGuideStep7Open(false);
                    // 延迟一下，确保 DOM 更新完成
                    setTimeout(() => {
                      // 查找目标元素
                      const targetElement = document.querySelector('[data-guide-target="positionListTabs"]');
                      if (targetElement) {
                        // 获取目标元素相对于视口的位置
                        const rect = targetElement.getBoundingClientRect();
                        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        
                        // 与标准版 Step6→7 同类：把 positionListTabs 滚到视口偏上，便于锚点 + 提示区（ProGuideStep8）
                        const DESIGN_WIDTH = 375;
                        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
                        
                        // 经验值：与历史设计稿 231/140 对齐，配合 clampGuideHighlightTopPx
                        const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
                        const highlightTop = clampGuideHighlightTopPx(isSmallScreen ? 140 * scale : 231 * scale);
                        const highlightPadding = 14 * scale;
                        const extraScrollUp = 120 * scale;
                        const scrollToPosition = currentScrollTop + (rect.top - highlightTop - highlightPadding) + extraScrollUp;
                        window.scrollTo(0, Math.max(0, scrollToPosition));
                      }
                    }, 50);
                  }} />
                )}

                {/* 专业版新手引导（第 8 页） */}
                {proGuideStep8Open && (
                  <ProGuideStep8 onSkip={closeAllGuides} onNext={() => {
                    setProGuideStep9Open(true);
                    setProGuideStep8Open(false);
                    // 延迟一下，确保 DOM 更新完成
                    setTimeout(() => {
                      // 查找目标元素
                      const targetElement = document.querySelector('[data-guide-target="historyButton"]');
                      if (targetElement) {
                        // 获取目标元素相对于视口的位置
                        const rect = targetElement.getBoundingClientRect();
                        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        
                        // 与标准版 Step7 同类：把 historyButton 滚到合适位置（ProGuideStep9 锚点）
                        const DESIGN_WIDTH = 375;
                        const scale = typeof window !== "undefined" ? (window.innerWidth || DESIGN_WIDTH) / DESIGN_WIDTH : 1;
                        
                        // 经验值：与历史设计稿 237/150 对齐，配合 clampGuideHighlightTopPx
                        const isSmallScreen = typeof window !== "undefined" && window.innerHeight <= 700;
                        const highlightTop = clampGuideHighlightTopPx(isSmallScreen ? 150 * scale : 237 * scale);
                        const extraScrollUp = 120 * scale;
                        const scrollToPosition = currentScrollTop + (rect.top - highlightTop) + extraScrollUp;
                        window.scrollTo(0, Math.max(0, scrollToPosition));
                      }
                    }, 50);
                  }} />
                )}

                {/* 专业版新手引导（第 9 页） */}
                {proGuideStep9Open && (
                  <ProGuideStep9 onSkip={closeAllGuides} onNext={closeAllGuides} />
                )}
              </div>
            </div>,
          document.body
        )
      : null;

  return (
    <>
      {!loading && (
        <>
          {guidePortal}
          <div ref={cfdMainRef} className={styles.cfdMain}>
          {/* Header组件 */}
          <Header viewMode={viewMode} onViewModeChange={handleViewModeChange} onOpenTutorial={handleOpenTutorial} variant="trade" hideMarketSwitch={hideMarketSwitch} />

          {/* 交易主内容区（含停盘蒙层）：与现货一致，tradingEnabled=false 时显示停盘；停盘时加 overflow-x 防露出 */}
          <div className={cx(styles.contentWithSuspended, { [styles.contentWithSuspendedWhenSuspended]: (store.market.currentConfig?.tradingEnabled === false || store.market.currentConfig?.state === "OFFLINE") })}>
            {/* K线图区域 - 标准模式在顶部显示 */}
            {viewMode === "standard" && (
              <div className={cx(styles.klineSection, styles.standardKlineSection)}>
                <Kline 
                  key="kline-top" 
                  viewMode={viewMode} 
                  onExpandChange={setIsKlineExpanded}
                  initialExpanded={isKlineExpanded}
                />
              </div>
            )}

            {/* 交易表单区域 */}
            <div className={cx(styles.tradeSection, { [styles.proLayout]: viewMode === "pro" })}>
              {viewMode === "standard" ? (
                <TradeForm
                  usePropOpenApi={usePropOpenApi}
                  onOpenPositionSuccess={handleRefreshPositionsAndOrders}
                  availableBalanceDisplayUnit={availableBalanceDisplayUnit}
                />
              ) : (
                <>
                  <div className={styles.tradeFormWrapper}>
                    <TradeFormPro
                      usePropOpenApi={usePropOpenApi}
                      onOpenPositionSuccess={handleRefreshPositionsAndOrders}
                      availableBalanceDisplayUnit={availableBalanceDisplayUnit}
                    />
                  </div>
                  <div className={styles.orderBookWrapper}>
                    <OrderBook />
                  </div>
                </>
              )}
            </div>

            {/* 持仓列表区域 */}
            <div className={styles.positionSection}>
              <PositionList ref={positionListRef} />
            </div>

            {/* K线图区域 - 专业模式始终在底部显示（无论展开还是收起） */}
            {viewMode === "pro" && (
              <div className={cx(styles.klineSection, { [styles.collapsed]: !isKlineExpanded }, styles.collapsedBottom)}>
                <Kline 
                  key="kline-bottom" 
                  viewMode={viewMode} 
                  onExpandChange={setIsKlineExpanded}
                  initialExpanded={isKlineExpanded}
                />
              </div>
            )}

            {/* 停盘蒙层：与现货一致，currentConfig.tradingEnabled=false 时覆盖上方交易区 */}
            <CPMT_suspended />
          </div>

          {/* 新手引导占位元素 - 用于补充高度，确保可以滚动到目标位置 */}
          {isAnyGuideOpen && (
            <div className={styles.guidePlaceholder} />
          )}
        </div>
        </>
      )}

      {!loading && (
        <>
          {store.trade.modalTransfer && (
            <AppModalTransfer {...store.trade.modalTransfer} updateProps={(props) => handleModalUpdateProps("modalTransfer", props)} />
          )}
        </>
      )}

      {!loading && <TradeFooter />}

      {loading && <XtLoading className={styles.loading} />}
    </>
  );
};

export default observer(CfdTrade);
